<template>
  <div class="industry-trend page-shell">
    <div class="toolbar">
      <span class="toolbar-label">所属行业</span>
      <el-select
        v-model="selectedIndustries"
        size="small"
        multiple
        filterable
        collapse-tags
        placeholder="请选择行业（可多选）"
        style="width: 360px"
      >
        <el-option
          v-for="item in industries"
          :key="item.name"
          :label="`${item.name}（${item.count}）`"
          :value="item.name"
          :disabled="isIndustryDisabled(item.name)"
        />
      </el-select>

      <span class="toolbar-label">区间</span>
      <el-date-picker
        v-model="dateRange"
        type="daterange"
        size="small"
        value-format="yyyy-MM-dd"
        range-separator="至"
        start-placeholder="开始"
        end-placeholder="结束"
        :picker-options="pickerOptions"
      />

      <el-button type="primary" size="small" :loading="loading" @click="onQuery">
        查询
      </el-button>
    </div>

    <el-alert
      v-if="result?.adjusted"
      type="info"
      :closable="false"
      show-icon
      class="sync-alert"
    >
      <template slot="title">
        已对齐至最近交易日：{{ result.start }} → {{ result.end }}
        （原选择 {{ result.requestedStart }} → {{ result.requestedEnd }}）
      </template>
    </el-alert>

    <div v-if="result" class="stats-bar">
      <span>{{ result.methodLabel }} · {{ result.start }} → {{ result.end }}</span>
      <span
        v-for="item in result.series"
        :key="item.industry"
        class="stat-item"
      >
        {{ item.industry }}
        {{ formatPct(item.periodReturn) }}%
        （{{ item.baseSampleSize }}/{{ item.stockCount }}）
      </span>
    </div>

    <el-alert
      v-if="error"
      :title="error"
      type="error"
      show-icon
      :closable="false"
      class="error-alert"
    />

    <div
      ref="chart"
      class="chart-box"
      v-loading="loading"
      element-loading-text="查询中..."
    />

    <div v-if="queried && !loading && !error && !result?.series?.length" class="empty-tip">
      无数据，请确认行业与日期区间
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { LineChart } from 'echarts/charts'
import {
  TitleComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  DataZoomComponent,
} from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { fetchIndustries, fetchIndustryTrend } from '../utils/industryApi.js'

echarts.use([
  LineChart,
  TitleComponent,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  DataZoomComponent,
  CanvasRenderer,
])

const DEFAULT_START_DATE = '2026-06-30'
const MAX_INDUSTRIES = 8

function formatDate(date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function todayDate() {
  return formatDate(new Date())
}

export default {
  name: 'IndustryTrend',
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      industries: [],
      selectedIndustries: [],
      dateRange: [DEFAULT_START_DATE, todayDate()],
      loading: false,
      queried: false,
      error: '',
      result: null,
      chart: null,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now()
        },
      },
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.$nextTick(() => {
          this.chart?.resize()
        })
      }
    },
  },
  async created() {
    await this.loadIndustries()
    if (this.selectedIndustries.length) {
      await this.onQuery()
    }
  },
  mounted() {
    this.initChart()
    window.addEventListener('resize', this.onResize, { passive: true })
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },
  methods: {
    isIndustryDisabled(name) {
      return (
        this.selectedIndustries.length >= MAX_INDUSTRIES
        && !this.selectedIndustries.includes(name)
      )
    },
    formatPct(v) {
      if (v == null || v === '') return '--'
      const n = Number(v)
      if (!Number.isFinite(n)) return '--'
      return `${n > 0 ? '+' : ''}${n.toFixed(2)}`
    },
    async loadIndustries() {
      try {
        this.industries = await fetchIndustries()
        if (!this.selectedIndustries.length && this.industries.length) {
          this.selectedIndustries = this.industries.slice(0, 3).map((item) => item.name)
        }
      } catch (err) {
        this.error = err.message || '加载行业列表失败'
      }
    },
    initChart() {
      if (!this.$refs.chart) return
      this.chart = echarts.init(this.$refs.chart)
      this.chart.setOption({
        title: {
          text: '请选择行业后查询',
          left: 'center',
          top: 'middle',
          textStyle: { color: '#909399', fontSize: 14, fontWeight: 400 },
        },
      })
    },
    onResize() {
      this.chart?.resize()
    },
    renderChart(result) {
      if (!this.chart) this.initChart()
      if (!this.chart || !result) return

      const dates = result.dates || []
      const series = (result.series || []).map((item) => {
        const byDate = new Map(item.points.map((p) => [p.date, p.index]))
        return {
          name: item.industry,
          type: 'line',
          showSymbol: false,
          smooth: true,
          data: dates.map((d) => byDate.get(d) ?? null),
        }
      })

      this.chart.setOption({
        title: { show: false },
        color: ['#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399', '#00C2B3', '#8B5CF6', '#F472B6'],
        tooltip: {
          trigger: 'axis',
          valueFormatter: (v) => (v == null ? '--' : Number(v).toFixed(2)),
        },
        legend: {
          type: 'scroll',
          top: 0,
        },
        grid: {
          left: 48,
          right: 24,
          top: 48,
          bottom: 56,
        },
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', height: 18, bottom: 8 },
        ],
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: {
            formatter(value) {
              const parts = String(value).split('-')
              return parts.length === 3 ? `${parts[1]}-${parts[2]}` : value
            },
          },
        },
        yAxis: {
          type: 'value',
          scale: true,
          name: '指数',
          axisLabel: {
            formatter: (v) => Number(v).toFixed(0),
          },
        },
        series,
      }, true)
      this.$nextTick(() => this.chart?.resize())
    },
    async onQuery() {
      if (!this.selectedIndustries.length) {
        this.$message.warning('请至少选择一个行业')
        return
      }
      if (this.selectedIndustries.length > MAX_INDUSTRIES) {
        this.$message.warning(`最多同时对比 ${MAX_INDUSTRIES} 个行业`)
        return
      }
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning('请选择日期区间')
        return
      }

      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const data = await fetchIndustryTrend({
          industries: this.selectedIndustries,
          start: this.dateRange[0],
          end: this.dateRange[1],
        })
        this.result = data
        this.renderChart(data)
      } catch (err) {
        this.result = null
        this.error = err.message || '查询失败'
        this.chart?.clear()
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.industry-trend {
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 120px);
}

.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.toolbar-label {
  font-size: 14px;
  color: #606266;
}

.stats-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
}

.stat-item {
  white-space: nowrap;
}

.sync-alert,
.error-alert {
  margin-bottom: 12px;
}

.chart-box {
  flex: 1;
  min-height: 420px;
  width: 100%;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

.empty-tip {
  padding: 24px 0;
  color: #909399;
  text-align: center;
}
</style>
